import {
  airLineTicketLanguageOptions,
  language as formLanguage,
  transferListLanguageOptions,
  utilityListLanguageOptions,
} from "../../components/dropdown/dropdown-data";
import { emycConst, emycEndpoints } from "../../utils/emycConstants";
import { derashConst, derashEndpoints } from "../../utils/derashConstants";
import {
  flyzagolConst,
  flyzagolEndpoints,
} from "../../utils/flyzagolConstants";
import { fuelConst } from "../../utils/fuelConstants";
import { hajjConst } from "../../utils/hajjConstants";

const languageIndex = (language) => {
  const index = Number(language);
  return Number.isInteger(index) && index >= 0 && index <= 5 ? index : 0;
};

const menuTitle = (language, index) =>
  transferListLanguageOptions[languageIndex(language)][index];

const localizedLabels = (translations, language) =>
  translations[languageIndex(language)].labels;

const baseQueryRequest = ({ reference }) => ({
  pnr: reference,
  hashedValue: "hashedValue",
});

export const fuelPaymentService = {
  id: "fuel",
  queryEndpoint: "fuelApp/services/searchFuelPnr",
  paymentEndpoint: "fuelApp/services/payFuelPayment",
  getTitle: (language) => menuTitle(language, 5),
  getLabels: (language) => localizedLabels(fuelConst, language),
  queryRequest: baseQueryRequest,
  mapQueryResponse: (payload) => ({
    carPlateNumber: payload.carPlateNumber,
    fuelType: payload.fuelType,
    commissionAmount: payload.commissionAmount,
    totalAmount: payload.totalAmount,
    stationName: payload.stationName,
    fuelAmount: payload.fuelAmount,
    transactionRef: payload.transactionRef,
  }),
  mapPaymentResponse: (payload) => ({
    amount: payload.amount,
    transactionId: payload.transactionId,
  }),
  paymentRequest: ({ reference, selectedAccount, userName }) => ({
    transactionRef: reference,
    channel: "Omni Plus",
    fromName: userName,
    fromAccount: selectedAccount,
    hashedValue: "hashedValue",
  }),
  detailFields: [
    { labelIndex: 6, field: "carPlateNumber" },
    { labelIndex: 4, field: "fuelType" },
    { labelIndex: 7, field: "commissionAmount" },
    { labelIndex: 3, field: "totalAmount" },
    { labelIndex: 5, field: "stationName" },
  ],
  confirmationMessage: (detail) =>
    `<b>Fuel payment of : </b> ${detail.fuelAmount} <br><br>` +
    `<b>Commission amount of : </b> ${detail.commissionAmount} <br><br>` +
    `<b>with reference: </b> ${detail.transactionRef} <br><br>`,
  successMessage: ({ payment, reference }) =>
    `Fuel payment of ${payment.amount} is successful with fuel reference ` +
    `${reference} and transaction reference number ${payment.transactionId} ` +
    `please confirm your transaction for the station attendant.`,
};

export const hajjPaymentService = {
  id: "hajj",
  continueLabelIndex: 6,
  queryEndpoint: "hajjApp/services/searchHajjPnr",
  paymentEndpoint: "hajjApp/services/payHajjPayment",
  getTitle: (language) => menuTitle(language, 10),
  getLabels: (language) => localizedLabels(hajjConst, language),
  queryRequest: baseQueryRequest,
  mapQueryResponse: (payload) => ({
    amount: payload.amount,
    name: payload.name,
    pnr: payload.pnr,
    servicePackage: payload.servicePackage,
  }),
  mapPaymentResponse: (payload) => ({
    amount: payload.amount,
    transactionId: payload.transactionId,
  }),
  paymentRequest: ({ reference, selectedAccount, userName }) => ({
    transactionRef: reference,
    channel: "Omni Plus",
    fromName: userName,
    fromAccount: selectedAccount,
    branchCode: "HQ",
    branchName: "HQ",
    userId: userName,
  }),
  detailFields: [
    { labelIndex: 7, field: "name" },
    { labelIndex: 4, field: "servicePackage" },
    { labelIndex: 3, field: "amount" },
    { labelIndex: 5, field: "pnr" },
  ],
  confirmationMessage: (detail) =>
    `<b>Hajj payment of : </b> ${detail.amount} <br><br>` +
    `<b>for Hujjaj : </b> ${detail.name} <br><br>` +
    `<b>with payment code : </b> ${detail.pnr} <br><br>`,
  successMessage: ({ payment, reference }) =>
    `Hajj payment of ${payment.amount} is successful with hajj reference ` +
    `${reference} and transaction reference number ${payment.transactionId} ` +
    `Thank you for working with Hijra Bank.`,
};

export const emycPaymentService = {
  id: "emyc",
  queryEndpoint: emycEndpoints.paymentQuery,
  paymentEndpoint: emycEndpoints.payment,
  getTitle: (language) => emycConst[languageIndex(language)].title,
  getLabels: (language) => localizedLabels(emycConst, language),
  queryRequest: baseQueryRequest,
  mapQueryResponse: (payload) => ({
    billId: payload.billId,
    amount: payload.amount,
    fullName: payload.fullName,
  }),
  mapPaymentResponse: (payload) => ({
    cbeTxnRef: payload.transactionId,
  }),
  paymentRequest: ({ detail, selectedAccount, userName }) => ({
    transactionRef: detail.billId,
    channel: "Omni Plus",
    fromName: userName,
    fromAccount: selectedAccount,
    hashedValue: "hashedValue",
  }),
  detailFields: [
    { labelIndex: 3, field: "billId" },
    { labelIndex: 4, field: "amount" },
    { labelIndex: 5, field: "fullName" },
  ],
  confirmationMessage: (detail, labels, language) => {
    const confirmationTitle =
      emycConst[languageIndex(language)].confirmationTitle;

    return (
      `<b>${confirmationTitle}</b><br><br>` +
      `<b>${labels[4]}:</b> ${detail.amount}<br><br>` +
      `<b>${labels[5]}:</b> ${detail.fullName}<br><br>` +
      `<b>${labels[3]}:</b> ${detail.billId}<br><br>`
    );
  },
  successMessage: ({ payment, detail }) =>
    `EMYC payment of ${detail.amount} for ${detail.fullName} with bill ID ` +
    `${detail.billId} is successful with transaction reference ` +
    `${payment.cbeTxnRef}.`,
};

export const derashPaymentService = {
  id: "derash",
  queryEndpoint: derashEndpoints.paymentQuery,
  paymentEndpoint: derashEndpoints.payment,
  getTitle: (language) => derashConst[languageIndex(language)].title,
  getLabels: (language) => localizedLabels(derashConst, language),
  requiredContextKeys: ["billerCode"],
  contextFields: [{ key: "billerName", labelIndex: 1 }],
  referenceLabelIndex: 2,
  searchLabelIndex: 4,
  queryFields: [{ key: "phoneNumber", labelIndex: 3, type: "tel" }],
  queryRequest: ({ billerCode, reference, queryInputValues }) => ({
    billerCode,
    pnr: reference,
    phoneNumber: queryInputValues.phoneNumber,
    channel: "Omni",
  }),
  mapQueryResponse: (payload) => ({
    billId: payload.billId,
    amount: payload.amount,
    customerName: payload.customerName,
    dueDate: payload.dueDate,
    billDescription: payload.billDescription,
  }),
  mapPaymentResponse: (payload) => ({
    paymentReference: payload.paymentReference,
    amount: payload.amount,
    transactionId: payload.transactionId,
    valueDate: payload.valueDate,
  }),
  paymentRequest: ({ detail, selectedAccount, userName }) => ({
    fromAccount: selectedAccount,
    fromName: userName,
    transactionRef: detail.billId,
    channel: "Omni",
  }),
  detailFields: [
    { labelIndex: 5, field: "billId" },
    { labelIndex: 6, field: "amount" },
    { labelIndex: 7, field: "customerName" },
    { labelIndex: 8, field: "dueDate" },
    { labelIndex: 9, field: "billDescription" },
  ],
  confirmationMessage: (detail, labels, language) => {
    const confirmationTitle =
      derashConst[languageIndex(language)].confirmationTitle;

    return (
      `<b>${confirmationTitle}</b><br><br>` +
      `<b>${labels[6]}:</b> ${detail.amount}<br><br>` +
      `<b>${labels[7]}:</b> ${detail.customerName}<br><br>` +
      `<b>${labels[5]}:</b> ${detail.billId}<br><br>`
    );
  },
  successMessage: ({ payment, detail }) =>
    `Derash payment of ${payment.amount} for ${detail.customerName} was ` +
    `successful. Payment reference: ${payment.paymentReference}. Transaction ` +
    `ID: ${payment.transactionId}. Value date: ${payment.valueDate}.`,
};

export const airlinePaymentService = {
  id: "airline",
  queryEndpoint: "FlyGateInfoRI/rest/flygate",
  paymentEndpoint: "FlyGateTransferRI/rest/flygate",
  getTitle: (language) =>
    utilityListLanguageOptions[languageIndex(language)][0],
  getLabels: (language) =>
    localizedLabels(airLineTicketLanguageOptions, language),
  getSearchLabel: (language) =>
    formLanguage[languageIndex(language)].button,
  autoConfirmQuery: true,
  serializeQueryRequest: true,
  queryRequest: ({ reference }) => ({ pnr: reference }),
  normalizeResponse: (response) => ({
    success: response.status === true,
    payload: response,
    error: response.statusDesc,
  }),
  mapQueryResponse: (payload) => ({
    orderId: payload.orderId,
    customerName: payload.customerName,
    amount: payload.amount,
  }),
  mapPaymentResponse: (payload) => ({
    statusDesc: payload.statusDesc,
  }),
  paymentRequest: ({ detail, selectedAccount, userName }) => ({
    orderId: detail.orderId,
    fromAccount: selectedAccount,
    fromAccountHolder: userName,
    amount: detail.amount,
  }),
  detailFields: [],
  confirmationMessage: (detail, labels, language, state) =>
    `<b>Utility: </b> ${
      transferListLanguageOptions[languageIndex(language)][1]
    } <br><br>` +
    `<b>PNR: </b> ${detail.orderId} <br><br>` +
    `<b>Customer name: </b> ${detail.customerName} <br><br>` +
    `<b>From: </b> ${state.userName} <br><br>` +
    `<b>${labels[0]}: </b> ${state.selectedAccount} <br><br>` +
    `<b>Amount: </b> ${detail.amount} <br><br>`,
  successMessage: ({ payment }) => payment.statusDesc,
};

export const flyzagolPaymentService = {
  id: "flyzagol",
  queryEndpoint: flyzagolEndpoints.paymentQuery,
  paymentEndpoint: flyzagolEndpoints.payment,
  getTitle: (language) => flyzagolConst[languageIndex(language)].title,
  getLabels: (language) => localizedLabels(flyzagolConst, language),
  queryRequest: ({ reference }) => ({ pnr: reference }),
  mapQueryResponse: (payload) => ({
    pnr: payload.pnr,
    amount: payload.amount,
    passengerName: payload.passengerName,
  }),
  mapPaymentResponse: (payload) => ({
    paymentReference: payload.paymentReference,
    fromAccount: payload.fromAccount,
    amount: payload.amount,
    transactionId: payload.transactionId,
    valueDate: payload.valueDate,
  }),
  paymentRequest: ({ reference, selectedAccount, userName }) => ({
    transactionRef: reference,
    fromAccount: selectedAccount,
    fromName: userName,
    channel: "OMNI",
  }),
  detailFields: [
    { labelIndex: 3, field: "pnr" },
    { labelIndex: 4, field: "passengerName" },
    { labelIndex: 5, field: "amount" },
  ],
  confirmationMessage: (detail, labels, language, state) => {
    const confirmationTitle =
      flyzagolConst[languageIndex(language)].confirmationTitle;

    return (
      `<b>${confirmationTitle}</b><br><br>` +
      `<b>${labels[3]}:</b> ${detail.pnr}<br><br>` +
      `<b>${labels[4]}:</b> ${detail.passengerName}<br><br>` +
      `<b>${labels[5]}:</b> ${detail.amount}<br><br>` +
      `<b>${labels[0]}:</b> ${state.selectedAccount}<br><br>`
    );
  },
  successMessage: ({ payment, detail }) =>
    `Flyzagol payment of ${payment.amount} for ${detail.passengerName} was ` +
    `successful. Payment reference: ${payment.paymentReference}. Transaction ` +
    `ID: ${payment.transactionId}. From account: ${payment.fromAccount}. ` +
    `Value date: ${payment.valueDate}.`,
};
